import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { sanitizeRedirectPath } from '../utils/redirectAfterLogin';

export default function ConfirmEmail() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmRegistration, resendConfirmationCode, requestPasswordReset } = useAuth();

  const email = location.state?.email || '';
  const isRecovery = location.state?.isRecovery || false;
  // Carried from Login/Register so the visitor returns to the page they started on.
  const redirectTo = sanitizeRedirectPath(location.state?.redirect);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(
    isRecovery
      ? t(
          'confirm.recoverySent',
          'This email is already registered. A new verification code has been sent — please check your inbox.'
        )
      : ''
  );
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await confirmRegistration(email, code);
      if (isRecovery) {
        // Auto-send password reset code so user goes straight to reset page
        try {
          await requestPasswordReset(email);
        } catch {
          // If reset request fails, still redirect — user can resend from reset page
        }
        setSuccess(
          t('confirm.recoverySuccess', 'Email verified! Redirecting to set your password...')
        );
        setTimeout(() => {
          navigate('/reset-password', { state: { email, redirect: redirectTo } });
        }, 2000);
      } else if (result.autoSignInStep === 'SIGNED_IN') {
        setSuccess(t('confirm.autoSignInSuccess', 'Email verified! Signing you in...'));
        setTimeout(() => {
          navigate(redirectTo || '/');
        }, 1500);
      } else {
        setSuccess(t('confirm.success', 'Email confirmed successfully! Redirecting to login...'));
        setTimeout(() => {
          navigate(redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login');
        }, 2000);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('confirm.failed', 'Confirmation failed. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setResending(true);

    try {
      await resendConfirmationCode(email);
      setSuccess(t('confirm.codeSent', 'Verification code sent! Check your email.'));
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('confirm.resendFailed', 'Failed to resend code. Please try again.');
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-brand p-8 text-center space-y-4">
            <h2 className="text-h3 font-bold text-charcoal">
              {t('confirm.invalidAccess', 'Invalid Access')}
            </h2>
            <p className="text-body text-charcoal/70">
              {t('confirm.pleaseRegister', 'Please register or login first.')}
            </p>
            <Link
              to="/register"
              className="inline-block font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              {t('confirm.goToRegister', 'Go to Register')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card Container */}
        <div className="bg-white rounded-xl shadow-brand p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-h2 font-bold text-charcoal">
              {t('confirm.title', 'Verify your email')}
            </h2>
            <p className="mt-3 text-body text-charcoal/70">
              {t('confirm.description', 'We sent a verification code to your email address:')}
            </p>
            <p className="mt-2 text-body font-semibold text-primary">{email}</p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="rounded-lg bg-primary-pale border border-primary/30 p-4">
                <p className="text-sm font-medium text-primary">{success}</p>
              </div>
            )}

            {/* Verification Code Input */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-charcoal mb-2">
                {t('confirm.code', 'Verification Code')}
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal text-center text-2xl tracking-widest placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder="000000"
                maxLength={6}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-6 border border-transparent text-body font-semibold rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? t('confirm.verifying', 'Verifying...')
                : t('confirm.verify', 'Verify Email')}
            </button>

            {/* Resend Code Button */}
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resending}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending
                  ? t('confirm.resending', 'Resending...')
                  : t('confirm.resend', "Didn't receive code? Resend")}
              </button>
            </div>

            {/* Back to Login Link */}
            <div className="text-center text-sm text-charcoal/60 pt-2">
              <Link
                to="/login"
                className="font-medium text-primary hover:text-primary/80 transition-colors"
              >
                {t('confirm.backToLogin', 'Back to login')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
